import { useAppSelector } from '@app/store/hooks';
import React, { useState } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  ScrollView,
  Alert,
  ActivityIndicator,
  Dimensions,
  Animated
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';


const { width: screenWidth } = Dimensions.get('window');

const MobileFeedbackForm = ({ 
  onSubmit, 
  onBack, 
  error = null, 
  title = "It looks like we're experiencing issues",
  subtitle = "Our team has been notified. If you'd like to help us understand what happened, please share your feedback.",
  showHeader = false 
}) => {

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [fadeAnim] = useState(new Animated.Value(1));
  const [scaleAnim] = useState(new Animated.Value(1));
  const userDetails = useAppSelector((state) => state.auth.userDetails);
  const [feedbackData, setFeedbackData] = useState({
    name: userDetails?.linkedBorrowerAccounts[0].accountHolderName || '',
    email:  userDetails?.linkedBorrowerAccounts[0].accountHolderEmail || '',
    description: ''
  });
  const validateForm = () => {
    const errors = {};
    
    if (!feedbackData.name.trim()) {
      errors.name = 'Name is required';
    }
    
    if (!feedbackData.description.trim()) {
      errors.description = 'Description is required';
    }
    
    if (feedbackData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(feedbackData.email.trim())) {
      errors.email = 'Please enter a valid email address';
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Animate form out
      Animated.parallel([
        Animated.timing(fadeAnim, {
          toValue: 0,
          duration: 500,
          useNativeDriver: true,
        }),
        Animated.timing(scaleAnim, {
          toValue: 0.8,
          duration: 500,
          useNativeDriver: true,
        }),
      ]).start();

      // Simulate API call delay
      await new Promise(resolve => setTimeout(resolve, 1000));

      setIsSubmitted(true);
      setIsSubmitting(false);

      // Animate success message in
      Animated.parallel([
        Animated.timing(fadeAnim, {
          toValue: 1,
          duration: 500,
          useNativeDriver: true,
        }),
        Animated.timing(scaleAnim, {
          toValue: 1,
          duration: 500,
          useNativeDriver: true,
        }),
      ]).start();

      // Call onSubmit with delay and then reset
      setTimeout(async () => {
        try {
          await onSubmit({
            ...feedbackData,
            error: error ? {
              message: error.message,
              stack: error.stack,
              name: error.name
            } : null,
            metadata: {
              timestamp: new Date().toISOString(),
              url: typeof window !== 'undefined' ? window.location.href : '',
              userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : ''
            }
          });
          
          setFeedbackData({ name: '', email: '', description: '' });
          setValidationErrors({});
        } catch (submitError) {
          console.error('Failed to submit feedback:', submitError);
          Alert.alert('Error', 'Failed to submit feedback. Please try again.');
          setIsSubmitted(false);
          setIsSubmitting(false);
        }
      }, 3000);

    } catch (submitError) {
      console.error('Failed to submit feedback:', submitError);
      Alert.alert('Error', 'Failed to submit feedback. Please try again.');
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFeedbackData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear validation error when user starts typing
    if (validationErrors[field]) {
      setValidationErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  const SuccessAnimation = () => (
    <Animated.View style={[
      styles.successContainer,
      {
        opacity: fadeAnim,
        transform: [{ scale: scaleAnim }]
      }
    ]}>
      <View style={styles.successIcon}>
        <Text style={styles.checkmark}>✓</Text>
      </View>
      <Text style={styles.successTitle}>Feedback Submitted!</Text>
      <Text style={styles.successMessage}>
        Thank you for your feedback. We appreciate you taking the time to help us improve.
      </Text>
      <View style={styles.processingIndicator}>
        <ActivityIndicator color="#34D399" size="small" />
        <Text style={styles.processingText}>Processing your feedback...</Text>
      </View>
    </Animated.View>
  );

  if (isSubmitted) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.centeredContent}>
          <SuccessAnimation />
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      <ScrollView style={styles.scrollView} showsVerticalScrollIndicator={false}>
        {showHeader && (
          <View style={styles.header}>
            <View style={styles.headerLeft}>
              <TouchableOpacity onPress={onBack} style={styles.backButton}>
                <Text style={styles.backArrow}>←</Text>
              </TouchableOpacity>
              <Text style={styles.headerTitle}>Feedback</Text>
            </View>
            <TouchableOpacity style={styles.helpButton}>
              <View style={styles.helpIcon}>
                <Text style={styles.helpIconText}>?</Text>
              </View>
              <Text style={styles.helpText}>NEED HELP?</Text>
            </TouchableOpacity>
          </View>
        )}

        <Animated.View style={[
          styles.content,
          {
            opacity: fadeAnim,
            transform: [{ scale: scaleAnim }]
          }
        ]}>
          {/* Illustration */}
          <View style={styles.illustration}>
            {/* UFO */}
            <View style={styles.ufoContainer}>
              <View style={styles.ufo}>
                <View style={styles.ufoTop} />
              </View>
              {/* Beam lines */}
              <View style={styles.beamCenter} />
              <View style={[styles.beamSide, styles.beamLeft]} />
              <View style={[styles.beamSide, styles.beamRight]} />
            </View>
            
            {/* Planet */}
            <View style={styles.planet}>
              <View style={styles.planetSpot1} />
              <View style={styles.planetSpot2} />
            </View>

            {/* Floating dots */}
            <View style={[styles.floatingDot, styles.dot1]} />
            <View style={[styles.floatingDot, styles.dot2]} />
            <View style={[styles.floatingDot, styles.dot3]} />
          </View>

          {/* Title and Subtitle */}
          <Text style={styles.mainTitle}>{title}</Text>
          <Text style={styles.subtitle}>{subtitle}</Text>

          {/* Error Info */}
          {error && (
            <View style={styles.errorContainer}>
              <Text style={styles.errorTitle}>Error Details:</Text>
              <Text style={styles.errorMessage}>{error.message}</Text>
            </View>
          )}

          {/* Form */}
          <View style={styles.form}>
            <View style={styles.inputContainer}>
              <TextInput
                style={[
                  styles.input,
                  validationErrors.name && styles.inputError
                ]}
                placeholder="Name *"
                placeholderTextColor="#86868b"
                value={feedbackData.name}
                onChangeText={(value) => handleInputChange('name', value)}
              />
              {validationErrors.name && (
                <Text style={styles.errorText}>{validationErrors.name}</Text>
              )}
            </View>
            
            <View style={styles.inputContainer}>
              <TextInput
                style={[
                  styles.input,
                  validationErrors.email && styles.inputError
                ]}
                placeholder="Email (optional)"
                placeholderTextColor="#86868b"
                value={feedbackData.email}
                onChangeText={(value) => handleInputChange('email', value)}
                keyboardType="email-address"
                autoCapitalize="none"
              />
              {validationErrors.email && (
                <Text style={styles.errorText}>{validationErrors.email}</Text>
              )}
            </View>
            
            <View style={styles.inputContainer}>
              <TextInput
                style={[
                  styles.input,
                  styles.textArea,
                  validationErrors.description && styles.inputError
                ]}
                placeholder="Please describe the issue you encountered *"
                placeholderTextColor="#86868b"
                value={feedbackData.description}
                onChangeText={(value) => handleInputChange('description', value)}
                multiline
                numberOfLines={4}
                textAlignVertical="top"
              />
              {validationErrors.description && (
                <Text style={styles.errorText}>{validationErrors.description}</Text>
              )}
            </View>

            <TouchableOpacity
              style={[styles.submitButton, isSubmitting && styles.submitButtonDisabled]}
              onPress={handleSubmit}
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <View style={styles.loadingContainer}>
                  <ActivityIndicator color="#ffffff" size="small" />
                  <Text style={styles.submitButtonText}>Submitting...</Text>
                </View>
              ) : (
                <Text style={styles.submitButtonText}>Submit Feedback</Text>
              )}
            </TouchableOpacity>
          </View>
        </Animated.View>
      </ScrollView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: '#f8f9fa',
    justifyContent: 'center',
    alignItems: 'center',
    height: 1000
  },
  scrollView: {
    flex: 1,
  },
  centeredContent: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    backgroundColor: '#ffffff',
    borderBottomWidth: 1,
    borderBottomColor: '#e5e5e7',
  },
  headerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  backButton: {
    marginRight: 8,
    padding: 4,
  },
  backArrow: {
    fontSize: 18,
    color: '#007AFF',
  },
  headerTitle: {
    fontSize: 17,
    fontWeight: '500',
    color: '#000000',
  },
  helpButton: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  helpIcon: {
    width: 16,
    height: 16,
    backgroundColor: '#25D366',
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 4,
  },
  helpIconText: {
    color: '#ffffff',
    fontSize: 12,
    fontWeight: 'bold',
  },
  helpText: {
    color: '#25D366',
    fontSize: 13,
    fontWeight: '500',
  },
  content: {
    padding: 24,
    maxWidth: 400,
    alignSelf: 'center',
    width: '100%',
  },
  illustration: {
    height: 120,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 40,
    position: 'relative',
  },
  ufoContainer: {
    alignItems: 'center',
    zIndex: 2,
  },
  ufo: {
    width: 80,
    height: 40,
    backgroundColor: '#7ab8f0',
    borderRadius: 40,
    transform: [{ rotate: '-10deg' }],
    shadowColor: '#7ab8f0',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 12,
    elevation: 8,
    position: 'relative',
  },
  ufoTop: {
    position: 'absolute',
    top: -5,
    left: '50%',
    marginLeft: -15,
    width: 30,
    height: 15,
    backgroundColor: '#a8c8ec',
    borderRadius: 15,
  },
  beamCenter: {
    position: 'absolute',
    top: 35,
    width: 2,
    height: 20,
    backgroundColor: '#7ab8f0',
    opacity: 0.7,
  },
  beamSide: {
    position: 'absolute',
    top: 35,
    width: 1,
    height: 15,
    backgroundColor: '#7ab8f0',
    opacity: 0.6,
  },
  beamLeft: {
    left: -8,
    transform: [{ rotate: '-20deg' }],
  },
  beamRight: {
    right: -8,
    transform: [{ rotate: '20deg' }],
  },
  planet: {
    position: 'absolute',
    bottom: 0,
    width: 100,
    height: 50,
    backgroundColor: '#a8c8ec',
    borderTopLeftRadius: 50,
    borderTopRightRadius: 50,
    zIndex: 1,
  },
  planetSpot1: {
    position: 'absolute',
    top: 16,
    left: 24,
    width: 20,
    height: 20,
    backgroundColor: '#7ab8f0',
    borderRadius: 10,
    opacity: 0.4,
  },
  planetSpot2: {
    position: 'absolute',
    top: 8,
    right: 32,
    width: 12,
    height: 12,
    backgroundColor: '#7ab8f0',
    borderRadius: 6,
    opacity: 0.3,
  },
  floatingDot: {
    position: 'absolute',
    backgroundColor: '#7ab8f0',
    borderRadius: 50,
    opacity: 0.6,
  },
  dot1: {
    top: 20,
    left: 20,
    width: 8,
    height: 8,
  },
  dot2: {
    top: 40,
    right: 30,
    width: 6,
    height: 6,
  },
  dot3: {
    top: 60,
    left: 60,
    width: 4,
    height: 4,
  },
  mainTitle: {
    fontSize: 24,
    fontWeight: '600',
    color: '#1d1d1f',
    textAlign: 'center',
    marginBottom: 12,
    lineHeight: 30,
  },
  subtitle: {
    fontSize: 16,
    color: '#86868b',
    textAlign: 'center',
    marginBottom: 32,
    lineHeight: 22,
  },
  errorContainer: {
    marginBottom: 24,
    padding: 16,
    backgroundColor: '#fef2f2',
    borderWidth: 1,
    borderColor: '#fecaca',
    borderRadius: 8,
  },
  errorTitle: {
    fontSize: 14,
    fontWeight: '500',
    color: '#991b1b',
    marginBottom: 4,
  },
  errorMessage: {
    fontSize: 12,
    color: '#dc2626',
  },
  form: {
    gap: 16,
  },
  inputContainer: {
    width: '100%',
  },
  input: {
    width: '100%',
    padding: 16,
    borderWidth: 1,
    borderColor: '#d2d2d7',
    borderRadius: 8,
    fontSize: 16,
    backgroundColor: '#ffffff',
    color: '#000000',
  },
  inputError: {
    borderColor: '#dc2626',
    borderWidth: 2,
  },
  errorText: {
    fontSize: 12,
    color: '#dc2626',
    marginTop: 4,
    marginLeft: 4,
  },
  textArea: {
    height: 100,
    textAlignVertical: 'top',
  },
  submitButton: {
    width: '100%',
    backgroundColor: '#007AFF',
    paddingVertical: 16,
    paddingHorizontal: 24,
    borderRadius: 12,
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 8,
    shadowColor: '#007AFF',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 12,
    elevation: 8,
  },
  submitButtonDisabled: {
    opacity: 0.5,
  },
  submitButtonText: {
    color: '#ffffff',
    fontSize: 17,
    fontWeight: '600',
  },
  loadingContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  successContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    padding: 40,
  },
  successIcon: {
    width: 80,
    height: 80,
    backgroundColor: '#34D399',
    borderRadius: 40,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 24,
    shadowColor: '#34D399',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 12,
    elevation: 8,
  },
  checkmark: {
    fontSize: 36,
    color: '#ffffff',
    fontWeight: 'bold',
  },
  successTitle: {
    fontSize: 24,
    fontWeight: '600',
    color: '#1d1d1f',
    textAlign: 'center',
    marginBottom: 12,
  },
  successMessage: {
    fontSize: 16,
    color: '#86868b',
    textAlign: 'center',
    marginBottom: 32,
    lineHeight: 22,
  },
  processingIndicator: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  processingText: {
    fontSize: 14,
    color: '#34D399',
    fontWeight: '500',
  },
});

export default MobileFeedbackForm;